export const MONTH_NAMES_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

/** Construye los años hacia atrás desde el actual. */
export function buildYearsBack(fromYear: number): number[] {
  const years: number[] = [];
  const current = new Date().getFullYear();
  for (let y = current; y >= fromYear; y--) years.push(y);
  return years;
}

/** Rellena el mes a dos dígitos. */
export function padMonth(month: number): string {
  return String(month).padStart(2, '0');
}

/** Extrae las partes de meses del valor. */
function monthParts(value: any): string[] {
  if (value == null) return [];
  return splitMonths(value);
}

/** Divide el valor de meses en partes de texto. */
function splitMonths(value: any): string[] {
  if (Array.isArray(value)) return value.map(String);
  return String(value).split(',');
}

/** Convierte el valor en meses válidos del 1 al 12. */
export function parseMonths(value: any): number[] {
  return keepValidMonths(monthParts(value).map(Number));
}

/** Conserva solo los meses dentro del rango válido. */
function keepValidMonths(list: number[]): number[] {
  const out: number[] = [];
  for (const item of list) pushValidMonth(out, item);
  return out;
}

/** Agrega el mes si está entre 1 y 12. */
function pushValidMonth(out: number[], item: number): void {
  if (item >= 1 && item <= 12) out.push(item);
}

/** Normaliza el valor de meses a texto o nulo. */
export function normalizeMonths(value: any): string | null {
  if (value === null || value === undefined) return null;
  return normalizeMonthsText(value);
}

/** Normaliza el texto de meses a lista canónica. */
function normalizeMonthsText(value: any): string {
  if (value === '') return '';
  return joinMonths(parseMonths(value));
}

/** Une la lista de meses en texto ordenado. */
export function joinMonths(list: number[]): string {
  if (!list.length) return '';
  return uniqueSorted(list).join(',');
}

/** Ordena los meses sin duplicados. */
export function uniqueSorted(list: number[]): number[] {
  return [...new Set(list)].sort((a, b) => a - b);
}

/** Etiqueta el periodo como mes y año en español. */
export function periodMonthLabel(period: any): string {
  if (!period) return '';
  return datedLabel(period);
}

/** Etiqueta por partes ISO o por fecha local. */
function datedLabel(period: any): string {
  const label = partsLabel(String(period).split('T')[0]);
  return label ?? fallbackLabel(period);
}

/** Etiqueta el año y mes de una fecha ISO. */
function partsLabel(str: string): string | null {
  const parts = str.split('-');
  if (parts.length < 2) return null;
  return monthYearLabel(Number(parts[0]), Number(parts[1]));
}

/** Etiqueta el mes válido o nulo si no aplica. */
function monthYearLabel(year: number, month: number): string | null {
  if (validYearMonth(year, month)) return `${MONTH_NAMES_ES[month - 1]} ${year}`;
  return null;
}

/** Indica si el año y mes son calendáricos. */
function validYearMonth(year: number, month: number): boolean {
  return !hasNaN(year, month) && monthInRange(month);
}

/** Indica si alguno de los valores no es número. */
function hasNaN(year: number, month: number): boolean {
  return isNaN(year) || isNaN(month);
}

/** Indica si el mes está entre 1 y 12. */
function monthInRange(month: number): boolean {
  return month >= 1 && month <= 12;
}

/** Etiqueta con fecha local o el texto original. */
function fallbackLabel(period: any): string {
  const date = new Date(period);
  if (isNaN(date.getTime())) return String(period);
  return `${MONTH_NAMES_ES[date.getMonth()]} ${date.getFullYear()}`;
}
